import React, { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { API_ENDPOINTS } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

interface ChangePasswordModalProps {
  show: boolean;
  onHide: () => void;
}

interface ChangePasswordResponse {
  status: {
    code: number;
    message: string;
  };
  data?: {
    id: number;
    email: string;
    name: string;
  };
  errors?: string[];
}

function ChangePasswordModal({ show, onHide }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const auth = useAuth();
  const toast = useToast();

  const handleClose = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors([]);
    onHide();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    // Client-side validation
    const validationErrors: string[] = [];

    if (!currentPassword) {
      validationErrors.push("Current password is required");
    }

    if (!newPassword) {
      validationErrors.push("New password is required");
    } else if (newPassword.length < 6 || newPassword.length > 128) {
      validationErrors.push(
        "New password must be between 6 and 128 characters",
      );
    }

    if (confirmPassword && newPassword !== confirmPassword) {
      validationErrors.push("New password and confirmation do not match");
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const requestBody = {
        user: {
          current_password: currentPassword,
          password: newPassword,
          ...(confirmPassword && { password_confirmation: confirmPassword }),
        },
      };

      // Use fetch directly to properly handle error response body
      const response = await fetch(API_ENDPOINTS.register, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth.token,
        },
        body: JSON.stringify(requestBody),
      });

      const data: ChangePasswordResponse = await response.json();

      if (response.ok && data.status.code === 200) {
        toast.showToast("Password changed successfully", "success");
        handleClose();

        // Prompt user to log back in due to JWT rotation
        setTimeout(() => {
          if (
            window.confirm(
              "For security, your session has been refreshed. Would you like to log in again now?",
            )
          ) {
            auth.logout(() => {
              window.location.href = "/login";
            });
          }
        }, 500);
      } else {
        // Handle error response (422 or other errors)
        let errorMessages: string[] = [];

        if (data.errors && Array.isArray(data.errors)) {
          errorMessages = data.errors;
        } else if (data.status?.message) {
          errorMessages = [data.status.message];
        } else {
          errorMessages = ["Failed to change password. Please try again."];
        }

        setErrors(errorMessages);
        toast.showToast("Failed to change password", "danger");
      }
    } catch (error: any) {
      // Handle network errors or JSON parsing errors
      setErrors(["Network error. Please try again."]);
      toast.showToast("Failed to change password", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Change Password</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {errors.length > 0 && (
            <Alert variant="danger">
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}

          <Form.Group className="mb-3" controlId="currentPassword">
            <Form.Label>Current Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={isSubmitting}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="newPassword">
            <Form.Label>New Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter new password (6-128 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isSubmitting}
              required
              minLength={6}
              maxLength={128}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="confirmPassword">
            <Form.Label>Confirm New Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
            />
            <Form.Text className="text-muted">
              Optional, but must match new password if provided
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Changing..." : "Change Password"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ChangePasswordModal;
